import React, { useMemo, useState } from 'react';
import FamilyFinderLayout from '../components/FamilyFinderLayout';
import ChildHeader from '../components/ChildHeader';
import NetworkHealthCard from '../components/NetworkHealthCard';
import GenogramPanel from '../components/GenogramPanel';
import NetworkRoster from '../components/NetworkRoster';
import SearchPanel from '../components/SearchPanel';
import ContactTimeline from '../components/ContactTimeline';
import MemberDetailDrawer from '../components/MemberDetailDrawer';
import AuditTrailPanel from '../components/AuditTrailPanel';
import { useFamilyFinder } from '../context/FamilyFinderContext';
import useGenogramNetworkMembers from '../hooks/useGenogramNetworkMembers';

const FamilyFinderPage = () => {
  const {
    child,
    members,
    contactEvents,
    auditEntries,
    searchResults,
    searchSources,
    activityByMember,
    health,
    loading,
    refresh,
    addMember,
    updateMemberProfile,
    logContactEvent,
    executeSearch
  } = useFamilyFinder();
  const { members: genogramMembers, activityByMember: genogramActivity } = useGenogramNetworkMembers();
  const usingGenogramRoster = genogramMembers.length > 0;
  const memberLookup = useMemo(
    () => new Map(members.map((member) => [member._id, member])),
    [members]
  );

  const [selectedMember, setSelectedMember] = useState(null);
  const [prefillMemberId, setPrefillMemberId] = useState('');

  const rosterMembers = useMemo(() => {
    const source = usingGenogramRoster ? genogramMembers : members;
    return source.slice().sort((a, b) => new Date(b.lastContactAt || 0) - new Date(a.lastContactAt || 0));
  }, [usingGenogramRoster, genogramMembers, members]);

  const rosterActivity = useMemo(
    () => (usingGenogramRoster ? genogramActivity : activityByMember),
    [usingGenogramRoster, genogramActivity, activityByMember]
  );

  const handleAddToNetwork = async (result) => {
    if (!child) return null;
    const member = await addMember({
      name: result.name,
      relationshipToChild: result.relationshipHint,
      role: 'candidate',
      sourceProvenance: [result.source, ...(result.provenance || [])],
      childId: child?._id,
      emails: result.email ? [result.email] : [],
      addresses: result.city ? [`${result.city}, ${result.state}`] : []
    });
    setSelectedMember(member);
    return member;
  };

  const handleQuickContact = (member) => {
    const targetMember = usingGenogramRoster ? memberLookup.get(member._id) : member;
    if (!targetMember) {
      // Genogram may expose members that are not yet synced to the network roster API
      // Skip quick contact until a matching network record exists.
      console.warn('No network record available for quick contact', member);
      return;
    }
    setSelectedMember(targetMember);
    setPrefillMemberId(targetMember._id);
  };

  const handleSelectMember = (member) => {
    if (usingGenogramRoster) {
      const existing = memberLookup.get(member._id);
      setSelectedMember(existing || member);
    } else {
      setSelectedMember(member);
    }
  };

  if (loading && !child) {
    return <div className="family-finder-loading">Loading Family Finder…</div>;
  }

  return (
    <>
      <FamilyFinderLayout
        header={<ChildHeader child={child} networkHealthScore={health.score} onRefresh={refresh} />}
        topBanner={<NetworkHealthCard health={health} />}
        genogram={<GenogramPanel />}
        roster={
          <NetworkRoster
            members={rosterMembers}
            selectedMemberId={selectedMember?._id}
            onSelectMember={handleSelectMember}
            onQuickContact={handleQuickContact}
            activityByMember={rosterActivity}
          />
        }
        search={
          <SearchPanel
            onSearch={executeSearch}
            results={searchResults}
            sources={searchSources}
            onAddToNetwork={handleAddToNetwork}
          />
        }
        contactTimeline={
          <ContactTimeline
            events={contactEvents}
            members={members}
            prefillMemberId={prefillMemberId}
            onAddContact={async (payload) => {
              const event = await logContactEvent({ ...payload, childId: child._id });
              setPrefillMemberId('');
              return event;
            }}
          />
        }
        auditTrail={<AuditTrailPanel auditEntries={auditEntries} />}
      />
      <MemberDetailDrawer
        member={selectedMember}
        onClose={() => setSelectedMember(null)}
        onSave={async (memberId, updates) => {
          const updated = await updateMemberProfile(memberId, updates);
          setSelectedMember(updated);
        }}
      />
    </>
  );
};

export default FamilyFinderPage;
